import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion } from 'framer-motion';

import { trackClickRegenerateConfirm } from '../../utils/analytics/analytics';
import { Creativity, RequestType, SectionName, TabName } from '../../utils/types';
import { colors } from '../../utils/colors';
import { InputDataSection } from '../InputDataSection';
import { FieldComponent } from '../minicomponents/FieldComponent';
import { GradientButton } from '../minicomponents/GradientButton';
import { IconClose, IconInfo, IconWarning } from '../minicomponents/icons';

interface RegeneratePopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (remarks: string) => void;
  title: string;
  descriptionKey?: string;
  buttonText: string;
  generationCost: number;
  creditsLeft: number;
  isRemarksMandatory?: boolean;
  // Analytics context
  sectionName?: SectionName;
  tabName?: TabName;
  itemName?: RequestType;
  creativityLevel?: Creativity;
  regenerationsCount?: number;
}

const RegeneratePopup: React.FC<RegeneratePopupProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  descriptionKey,
  buttonText,
  generationCost,
  creditsLeft,
  isRemarksMandatory = true,
  sectionName,
  tabName,
  itemName,
  creativityLevel,
  regenerationsCount,
}) => {
  const [prompt, setPrompt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSubmit = useCallback(() => {
    if (prompt.trim().length >= 3 || !isRemarksMandatory) {
      // Track regenerate confirmation analytics
      if (sectionName && tabName && itemName) {
        trackClickRegenerateConfirm(
          sectionName,
          tabName,
          itemName,
          generationCost,
          creativityLevel,
          prompt.trim() || undefined,
          regenerationsCount
        );
      }

      onSubmit(prompt);
      onClose();
    } else {
      setError(t('app.labels.MustBeAtLeast3CharactersLong'));
    }
  }, [
    prompt,
    onSubmit,
    onClose,
    sectionName,
    tabName,
    itemName,
    generationCost,
    creativityLevel,
    regenerationsCount,
  ]);

  const handleClose = () => {
    setPrompt('');
    setError(null);
    onClose();
  };

  const handlePromptChange = (value: string) => {
    setPrompt(value);
    if (value.trim().length >= 3 && error) {
      setError(null);
    }
  };

  const navigateToSubscriptionPage = async () => {
    navigate('/app/profile/subscription');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: motion.div,
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' },
        transition: { type: 'spring', stiffness: 300, damping: 30 },
        sx: {
          maxHeight: 'calc(100vh - 104px)',
          marginTop: '52px',
          marginBottom: '52px',
          display: 'flex',
          flexDirection: 'column',
          width: 672,
        },
      }}
    >
      <Grid container size={12} sx={{ p: 3, pb: 2, alignItems: 'center' }}>
        <Grid size={'grow'}>
          <Typography variant="h5">{title}</Typography>
        </Grid>
        <Grid size={'auto'} justifyContent="flex-end">
          <IconButton onClick={handleClose}>
            <IconClose />
          </IconButton>
        </Grid>
      </Grid>

      <Divider />

      <DialogContent sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <Grid container gap={3}>
          {descriptionKey && t(descriptionKey) && (
            <Grid size={12}>
              <Stack spacing={1} direction={'row'} className="customDescription">
                <IconInfo />
                <Typography variant="body1" sx={{ pt: '3px' }}>
                  {t(descriptionKey)}
                </Typography>
              </Stack>
            </Grid>
          )}

          <Grid size={12}>
            <InputDataSection
              isMoreOptionsForceExpanded={true}
              popupMode="regenerate"
            />
          </Grid>
          <Grid size={12}>
            <FieldComponent
              fieldValue={prompt}
              onEdit={handlePromptChange}
              fieldLabel={t('app.labels.remarks')}
              placeholder={t('app.labels.remarksPlaceholder')}
              error={error}
              sx={{ color: 'secondary.text' }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {creditsLeft >= generationCost ? (
          <Stack direction="row" spacing={2}>
            <GradientButton
              buttonText={buttonText}
              onSubmitButtonHandler={handleSubmit}
              disabledTooltipText={error || ''}
            />
            <Button onClick={handleClose}>{t('app.buttons.cancel')}</Button>
          </Stack>
        ) : (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            className="customAlertMessage"
          >
            <Stack spacing={1}>
              <Stack direction="row" spacing={1}>
                <IconWarning />
                <Typography sx={{ pt: '3px' }}>
                  <strong>{t('app.popups.generate.notEnoughCredits')}</strong>
                </Typography>
              </Stack>
              <Typography sx={{ color: colors.text.secondary, fontSize: '90%' }}>
                {t('app.popups.generate.upgradeInvitation')}
              </Typography>
            </Stack>
            <GradientButton
              buttonText={t('website.upgradePopup.buttonUpgrade')}
              sx={{ minWidth: 100 }}
              onSubmitButtonHandler={navigateToSubscriptionPage}
              disabledTooltipText={error || ''}
            />
            <Button onClick={handleClose}>{t('app.buttons.cancel')}</Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RegeneratePopup;
